import { useEffect, useMemo, useState } from "react";
import useViews from "@/react/hooks/useViews";
import { Globe, Search, X } from "lucide-react";
import { cn } from "@/modern/utils";
import { Input } from "@/modern/components/ui/input";
import { VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";
import {
  MarketingStackWorkflowStrip,
  marketingStackCardClassName,
  marketingStackContentInsetClassName,
  marketingStackPageClassName,
} from "@/react/components/marketingStack";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/modern/components/ui/resizable";
import { useMarketingPages } from "./hooks/useMarketingPages";
import { MarketingPageCard } from "./components/MarketingPageCard";
import { MarketingPageDetailPanel } from "./components/MarketingPageDetailPanel";
import {
  MarketingPagesProvider,
  useMarketingPagesContext,
} from "./context/MarketingPagesContext";

const MarketingPagesViewContent = () => {
  const { pages } = useMarketingPages();
  const { selectedPage, toggleSelectedPage } = useMarketingPagesContext();
  const viewManagerState = useViews();
  const [query, setQuery] = useState("");

  const highlightIdRaw = viewManagerState?.selectedView?.otherParams?.marketingPageId;
  const highlightId = highlightIdRaw != null ? Number(highlightIdRaw) : null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pages;
    return pages.filter((p) => {
      const t = `${p.title ?? ""} ${p.slug ?? ""} ${p.url ?? ""}`.toLowerCase();
      return t.includes(q);
    });
  }, [pages, query]);

  useEffect(() => {
    if (highlightId == null || Number.isNaN(highlightId)) return;
    const page = pages.find((p) => p.id === highlightId);
    if (page && selectedPage?.id !== page.id) {
      toggleSelectedPage(page);
    }
  }, [highlightId, pages, selectedPage?.id, toggleSelectedPage]);

  useEffect(() => {
    if (pages.length === 0) return;
    const newest = pages[0];
    if (!newest?.createdAt) return;
    const age = Date.now() - new Date(newest.createdAt).getTime();
    if (age > 30_000) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`marketing-page-row-${newest.id}`);
      el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }, 150);
    return () => clearTimeout(t);
  }, [pages]);

  useEffect(() => {
    if (highlightId == null || Number.isNaN(highlightId)) return;
    const scrollToCard = () => {
      const el = document.getElementById(`marketing-page-row-${highlightId}`);
      el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    };
    scrollToCard();
    const t = setTimeout(scrollToCard, 300);
    return () => clearTimeout(t);
  }, [highlightId, filtered]);

  return (
    <div className={marketingStackPageClassName}>
      <MarketingStackWorkflowStrip activeView={VIEW_TYPES.marketingPages} />
      <div className={marketingStackContentInsetClassName}>
        <div className={marketingStackCardClassName()}>
          <div className="flex min-h-[3rem] shrink-0 flex-wrap items-center gap-4 border-b border-border bg-accent/80 px-4">
        <div className="relative w-52 shrink-0">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-3 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages…"
            variant="background"
            inputSize="sm"
            className={cn(
              "h-8 w-full rounded-full border border-border bg-card py-0 pl-8 text-xs leading-none text-card-foreground md:text-xs",
              "placeholder:text-muted-foreground",
              "!ring-0 !ring-offset-0 focus-visible:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary/20",
              query ? "pr-8" : "pr-3.5",
            )}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent/40 hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
          </div>

          <div className="min-h-0 flex-1 overflow-hidden">
        <ResizablePanelGroup
          key={selectedPage ? "marketing-pages-split" : "marketing-pages-full"}
          orientation="horizontal"
          className="h-full min-w-0"
        >
          <ResizablePanel
            id="marketing-pages-grid"
            maxSize={selectedPage ? "75%" : "100%"}
          >
            <div className="h-full min-w-0 overflow-y-auto">
              <div className="flex min-h-full flex-col">
                {filtered.length === 0 && query === "" ? (
                  <div className="relative flex flex-1 flex-col items-center justify-center overflow-hidden px-6 py-12 text-center">
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-muted text-muted-foreground">
                      <Globe className="h-5 w-5" />
                    </div>
                    <h3 className="text-base font-medium text-foreground">No published pages yet</h3>
                    <p className="mt-2 max-w-xs text-sm leading-relaxed text-muted-foreground">
                      Publish from a marketing work file in <span className="font-medium text-foreground">Work Files</span>. Pages
                      show up here after publishing finishes and data syncs to this board.
                    </p>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-card/50 m-4 p-12 text-center">
                    <p className="text-sm font-medium text-foreground">No pages match</p>
                    <p className="text-xs text-muted-foreground">Try a different search or clear the box above.</p>
                    <button
                      type="button"
                      onClick={() => setQuery("")}
                      className="mt-2 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                    >
                      Clear all
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,17.5rem),1fr))] gap-4 p-4">
                    {filtered.map((p) => (
                      <MarketingPageCard
                        key={p.id}
                        page={p}
                        highlight={highlightId != null && highlightId === Number(p.id)}
                        isSelected={selectedPage?.id === p.id}
                        onToggleSelect={toggleSelectedPage}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </ResizablePanel>

          {selectedPage && (
            <>
              <ResizableHandle withHandle />
              <ResizablePanel
                id="marketing-page-detail"
                defaultSize="35%"
                minSize="28%"
                maxSize="50%"
              >
                <div className="box-border flex h-full flex-col pt-4 pr-3 pb-4">
                  <div className="ml-3.5 flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-md">
                    <MarketingPageDetailPanel />
                  </div>
                </div>
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
          </div>
        </div>
      </div>
    </div>
  );
};

const MarketingPagesView = () => (
  <MarketingPagesProvider>
    <MarketingPagesViewContent />
  </MarketingPagesProvider>
);

export default MarketingPagesView;
